import React, { Component, Fragment } from 'react'
import { observer } from 'mobx-react'
import { observable, action, makeObservable } from 'mobx'
import { Nav, NavItem, NavLink } from 'reactstrap'
import { FacebookProvider, Comments } from 'react-facebook'
import classNames from 'classnames'

import { COURSE_THEME_STYLE } from 'constants/themes.constants'
import { getNestedChildren } from 'utils/product.utils'
import { getSearchParams } from 'utils/queryString.utils'
import Comment from './Comment'
import CommentsForm from './CommentsForm'
import CustomCommentsContainer from './views/CustomCommentsContainer'
import DefaultCommentsContainer from './views/DefaultCommentsContainer'

const COMMENT_TABS = {
  comments: 'comments',
  facebook: 'facebook',
}

@observer
class LessonComments extends Component {
  @observable activeTab = COMMENT_TABS.comments
  @observable commentId = getSearchParams()['comment_id']
  @observable showCommentsArea = false
  @observable replyId = ''

  constructor(props) {
    super(props)
    makeObservable(this)
  }

  @action onCommentsTabClick = () => {
    this.activeTab = COMMENT_TABS.comments
  }

  @action onFBCommentsTabClick = () => {
    this.activeTab = COMMENT_TABS.facebook
  }

  @action toggleCommentsArea = (value) => (this.showCommentsArea = value)

  @action setReplyId = (replyId) => (this.replyId = replyId)

  fetchComments() {
    const {
      courseViewsStore: { courseSession, activeLesson },
      commentsStore,
    } = this.props

    const courseSessionId = courseSession.id
    const lessonId = activeLesson.id

    commentsStore.fetchFullList({
      courseSessionId,
      lessonId,
    })
  }

  onAddComment = (commentValue, commentId) => {
    const {
      courseViewsStore: { courseSession, activeLesson },
      commentsStore,
    } = this.props

    const courseSessionId = courseSession.id
    const lessonId = activeLesson.id

    commentsStore
      .createItem({
        courseSessionId,
        lessonId,
        parentId: commentId,
        body: commentValue,
      })
      .then(() => {
        this.fetchComments()
      })
  }

  onEditComment = (commentId, data) => {
    const {
      courseViewsStore: { courseSession, activeLesson },
      commentsStore,
    } = this.props

    const courseSessionId = courseSession.id
    const lessonId = activeLesson.id

    commentsStore
      .updateItem(commentId, {
        courseSessionId,
        lessonId,
        body: data,
      })
      .then(() => {
        this.fetchComments()
      })
  }

  onDeleteComment = (commentId) => {
    const {
      courseViewsStore: { courseSession, activeLesson },
      commentsStore,
    } = this.props

    const courseSessionId = courseSession.id
    const lessonId = activeLesson.id

    commentsStore
      .deleteItem(commentId, {
        courseSessionId,
        lessonId,
      })
      .then(() => {
        this.fetchComments()
      })
  }

  render() {
    const {
      courseViewsStore: {
        activeLesson: { commentsAllowed, fbCommentsAllowed, fbUrl },
        viewConfig: {
          layout,
          isPayerCabinet,
          facebookAppId,
          sellerId,
          avatarUrl,
          currentProfileId,
          currentProfileType,
        },
      },
      commentsStore: { list: comments },
      sellerFontsStore,
      payerStore,
      sellerStore,
    } = this.props

    const commentTabActive = this.activeTab === COMMENT_TABS.comments
    const fbCommentTabActive = this.activeTab === COMMENT_TABS.facebook

    const customView = layout === COURSE_THEME_STYLE.custom
    const commentsTree = getNestedChildren(comments, null)

    const commentListClasses = classNames('comment-list', {
      'comment-list--with-bottom-padding': this.showCommentsArea || this.replyId,
    })

    const sellerFullName = [sellerStore.item.userProfile?.firstName, sellerStore.item.userProfile?.lastName]
      .filter(Boolean)
      .join(' ')

    const commentUserName = isPayerCabinet
      ? payerStore.item.publicName || payerStore.item.fullName
      : sellerStore.item.userProfile?.publicName || sellerFullName

    const commentsContent = (
      <Fragment>
        <Nav tabs className='inpage-nav-menu comments-nav'>
          {commentsAllowed && (
            <NavItem>
              <NavLink href='#' onClick={this.onCommentsTabClick} active={commentTabActive}>
                {I18n.t('shared.common.comments')}
              </NavLink>
            </NavItem>
          )}

          {fbCommentsAllowed && (
            <NavItem>
              <NavLink href='#' onClick={this.onFBCommentsTabClick} active={fbCommentTabActive}>
                {I18n.t('react.shared.comments.facebookComments')}
              </NavLink>
            </NavItem>
          )}
        </Nav>

        {commentTabActive && commentsAllowed && (
          <Fragment>
            <CommentsForm
              fontFamilyList={sellerFontsStore.froalaFonts}
              addComment={this.onAddComment}
              showCommentsArea={this.showCommentsArea}
              toggleCommentsArea={this.toggleCommentsArea}
              commentUserName={commentUserName}
              {...{
                avatarUrl,
                customView,
                isPayerCabinet,
              }}
            />
            <div className={commentListClasses}>
              {commentsTree &&
                commentsTree.map((comment) => (
                  <Comment
                    key={`comment${comment.id}`}
                    comment={comment}
                    sellerId={sellerId}
                    commentId={this.commentId}
                    currentProfileId={currentProfileId}
                    currentProfileType={currentProfileType}
                    addComment={this.onAddComment}
                    editComment={this.onEditComment}
                    deleteComment={this.onDeleteComment}
                    avatarUrl={avatarUrl}
                    isPayerCabinet={isPayerCabinet}
                    customView={customView}
                    fontFamilyList={sellerFontsStore.froalaFonts}
                    showCommentsArea={this.showCommentsArea}
                    toggleCommentsArea={this.toggleCommentsArea}
                    replyId={this.replyId}
                    setReplyId={this.setReplyId}
                  />
                ))}
            </div>
          </Fragment>
        )}

        {fbCommentTabActive && fbCommentsAllowed && (
          <FacebookProvider appId={facebookAppId}>
            <Comments href={fbUrl} fontFamilyList={sellerFontsStore.froalaFonts} />
          </FacebookProvider>
        )}
      </Fragment>
    )

    return (
      <Fragment>
        {customView ? (
          <CustomCommentsContainer>{commentsContent}</CustomCommentsContainer>
        ) : (
          <DefaultCommentsContainer>{commentsContent}</DefaultCommentsContainer>
        )}
      </Fragment>
    )
  }
}

LessonComments.displayName = 'LessonComments'

export default LessonComments
